import React, { useState } from "react";
import { SECTION_LABELS } from "@/lib/landingDefaults";
import { ICONS } from "@/components/landing/Sections";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowUp, ArrowDown, GripVertical, Smartphone } from "lucide-react";

const ICON_KEYS = Object.keys(ICONS);

export default function PortalSettingsCard({ portal, onChange }) {
  const [drag, setDrag] = useState(null);
  const { order, sections } = portal;
  const setSec = (id, patch) => onChange({ ...portal, sections: { ...sections, [id]: { ...sections[id], ...patch } } });
  const moveTo = (from, to) => {
    if (from === to || to < 0 || to >= order.length) return;
    const o = [...order];
    o.splice(to, 0, o.splice(from, 1)[0]);
    onChange({ ...portal, order: o });
  };

  return (
    <div className="w-card w-card-bordered p-4" data-testid="cms-portal-card">
      <div className="flex items-center gap-2 font-semibold"><Smartphone className="h-4 w-4" /> Customer portal sections</div>
      <p className="text-xs mt-1 mb-3" style={{ color: "var(--text-muted)" }}>
        Same content as the landing page. Expanded = always visible in the main area; Icon = opens in a panel from the icon bar
        (bottom bar on mobile). Drag rows or use the arrows to reorder. Goes live on Publish.
      </p>
      <ul className="space-y-2">
        {order.map((id, i) => {
          const s = sections[id];
          const I = ICONS[s.icon] || ICONS.star;
          return (
            <li key={id} draggable onDragStart={() => setDrag(i)} onDragOver={(e) => e.preventDefault()}
                onDrop={() => { moveTo(drag, i); setDrag(null); }} onDragEnd={() => setDrag(null)}
                className={`flex flex-wrap items-center gap-2 rounded-lg p-2 ${drag === i ? "opacity-50" : ""}`}
                style={{ border: "1px solid var(--border)", background: "var(--surface)" }} data-testid={`cms-portal-row-${id}`}>
              <GripVertical className="h-4 w-4 cursor-grab" style={{ color: "var(--text-muted)" }} aria-hidden />
              <span className="w-36 text-sm font-medium">{i + 1}. {SECTION_LABELS[id]}</span>
              <label className="flex items-center gap-1.5 text-xs">
                <Switch checked={s.show} onCheckedChange={(v) => setSec(id, { show: v })} data-testid={`cms-portal-show-${id}`} />
                {s.show ? "On" : "Off"}
              </label>
              <Select value={s.mode} onValueChange={(v) => setSec(id, { mode: v })} disabled={!s.show}>
                <SelectTrigger className="w-28 h-8" data-testid={`cms-portal-mode-${id}`}><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="expanded">Expanded</SelectItem><SelectItem value="icon">Icon</SelectItem></SelectContent>
              </Select>
              <Select value={s.icon} onValueChange={(v) => setSec(id, { icon: v })} disabled={!s.show}>
                <SelectTrigger className="w-36 h-8" data-testid={`cms-portal-icon-${id}`}>
                  <span className="!flex items-center gap-1.5 whitespace-nowrap"><I className="h-4 w-4 flex-shrink-0" />{s.icon}</span>
                </SelectTrigger>
                <SelectContent>
                  {ICON_KEYS.map((k) => { const K = ICONS[k]; return <SelectItem key={k} value={k}><span className="inline-flex items-center gap-2"><K className="h-4 w-4" />{k}</span></SelectItem>; })}
                </SelectContent>
              </Select>
              <Input value={s.label} maxLength={16} onChange={(e) => setSec(id, { label: e.target.value })} disabled={!s.show}
                     className="w-32 h-8" placeholder="Label" data-testid={`cms-portal-label-${id}`} />
              <span className="ml-auto flex">
                <Button type="button" size="sm" variant="ghost" disabled={!i} onClick={() => moveTo(i, i - 1)} aria-label="Move up"
                        data-testid={`cms-portal-up-${id}`}><ArrowUp className="h-4 w-4" /></Button>
                <Button type="button" size="sm" variant="ghost" disabled={i === order.length - 1} onClick={() => moveTo(i, i + 1)}
                        aria-label="Move down" data-testid={`cms-portal-down-${id}`}><ArrowDown className="h-4 w-4" /></Button>
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
